import React from 'react';
import { Box } from '@mui/material';
import { useRecoilState, useRecoilValue } from 'recoil';
import { endPoints } from 'api/endpoints';
import axios from 'api/request';
import { useMutation } from 'react-query';
import Select from 'pages/reference-implementation/components/select';
import { componentGroupsState, OrderPayload, urlParams } from 'pages/reference-implementation/provisioning/RI003AWS/store';
import { InputLabel } from 'pages/reference-implementation/components/input-label';
import { selectedEnv } from '../../utils/constant';
import { getComponentsFromSchema } from '../../utils';

export const Environments = () => {
    const [isTouched, setIsTouched] = React.useState(false);
    const [order, setOrder] = useRecoilState(OrderPayload);
    const componentGroups = useRecoilValue(componentGroupsState);
    const [options, setOptions] = React.useState([]);
    const [params] = useRecoilState(urlParams);
    const environmentApi = useMutation(() => axios.get(endPoints.riServices.COMMON.environments));
    const awsLandingZoneApi = useMutation((payload) => axios.post(endPoints.aws.lookups.acctVendingLandingZone, payload));
    const disasterRecoveryApi = useMutation((payload) => axios.post(endPoints.ssp.vm.disasterRecoveryOptions, payload));

    const setEnv = async (environment, envShortName) => {
        if (order?.environment !== environment) {
            let landingZoneDetails = null;
            let metallic = null;
            let backupTier = null;

            const hasBaseFields = order?.appProfile && order?.sector && order?.department && order?.appRegion && environment;
            const shouldCallLandingZone = hasBaseFields;

            if (shouldCallLandingZone) {
                try {
                    const response = await awsLandingZoneApi.mutateAsync({
                        applicationServiceName: order.appProfile,
                        sector: order.sector,
                        environment,
                        department: order.department,
                        applicationRegion: order.appRegion,
                    });
                    landingZoneDetails = response?.data || null;
                } catch (e) {
                    landingZoneDetails = null;
                }
            }
            if (order?.drPriority && environment && order?.appTierCode) {
                try {
                    const response = await disasterRecoveryApi.mutateAsync({
                        drPriority: order.drPriority,
                        environment,
                        appTierCode: order.appTierCode,
                    });
                    metallic = response?.data?.metallic || null;
                    backupTier = response?.data?.backupTier || null;
                } catch (e) {
                    metallic = null;
                    backupTier = null;
                }
            }
            setOrder({
                ...order,
                environment,
                envShortName,
                landingZoneDetails,
                metallic,
                backupTier,
                ...(environment?.toLowerCase() === 'production' && { nonPrivilegedADGroup: 'null' }),
                ...(environment?.toLowerCase() !== 'production' && {
                    nonPrivilegedADGroup: order?.nonPrivilegedADGroup === 'null' ? 'No' : order?.nonPrivilegedADGroup,
                }),
                businessAppService: 'Yes',
                appService: null,
                appUrl: null,
                isaNumber: null,
                iteAppService: null,
                program: null,
                resourceMgmt: {
                    [selectedEnv]: {
                        components: getComponentsFromSchema({ componentGroups, particularPrompt: ['mandatoryComponents'] })?.[0]?.components || [],
                    },
                },
            });
        }
    };

    const resetOptions = () => {
        setOptions([]);
    };

    const isExistsInOptions = (environmentValue) => options.find((option) => environmentValue === option.value);

    const handleOnSelectChange = (selected) => setEnv(selected.value, selected?.data?.backendName);

    React.useEffect(() => {
        if (environmentApi?.isSuccess) {
            setOptions(environmentApi?.data?.data?.map((app) => ({ label: app?.displayName, value: app?.displayName, data: app })));
        }
    }, [environmentApi?.isSuccess]);

    React.useEffect(() => {
        if (options && options?.length === 1 && !order?.environment) {
            setOrder({ ...order, environment: options?.[0].value, envShortName: options?.[0].data?.backendName });
        }
        if (options && options?.length && order?.environment) {
            const environment = isExistsInOptions(order?.environment);
            setOrder({ ...order, environment: environment?.value, envShortName: environment?.data?.backendName });
        }
    }, [options]);

    React.useEffect(() => {
        resetOptions();
        environmentApi.mutate();
    }, []);

    React.useEffect(() => {
        if (!order?.environment && order?.isTouched) setIsTouched(true);
        else setIsTouched(false);
    }, [order?.environment, order?.isTouched]);

    return (
        <Box display="flex" alignItems="center" gap={1}>
            <Select
                dataTestId="env-auto"
                key="environment"
                label={<InputLabel label="Environment" isRequired />}
                value={order?.environment || null}
                options={options}
                handleOnSelect={(value) => handleOnSelectChange(value)}
                disabled={environmentApi.isLoading || awsLandingZoneApi?.isLoading || disasterRecoveryApi?.isLoading || params?.journeyId}
                isLoading={environmentApi.isLoading || awsLandingZoneApi?.isLoading || disasterRecoveryApi?.isLoading}
                isRequired={isTouched}
            />
        </Box>
    );
};

export default React.memo(Environments);
